// Forms builder (intake admin): master-detail over /api/forms.
//
// Left card: the tenant's intake forms (+ "New form"). Selecting one loads the
// right card: its ordered fields (GET /api/forms/:slug) plus an "Add field"
// form (POST /api/forms/:id/fields). Field kinds + branching mirror forms-db.
//
// Deliberately NOT offered (no edge route exists yet):
//   - publish toggle  — forms-db.publishForm is not exposed by ac-edge
//   - portal toggle   — POST /api/forms does not accept portal_enabled
// New forms therefore start unpublished + internal-only; the pills still
// render whatever the API reports so existing flags stay visible.

import { useEffect, useState, type DragEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  addFormField,
  API_BASE,
  createForm,
  deleteForm,
  deleteFormField,
  getFormBySlug,
  getFormStats,
  listFormSubmissions,
  listForms,
  listProjects,
  listStatuses,
  publishForm,
  updateForm,
  updateFormField,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate } from "../lib/format";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { confirmDialog } from "../components/ConfirmDialog";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import type {
  AddFormFieldInput,
  UpdateFormFieldInput,
  FormBranchingRule,
  FormDetailRow,
  FormFieldKind,
  FormFieldRow,
  FormTargetKind,
} from "../lib/types";

const FIELD_KINDS: FormFieldKind[] = ["text", "textarea", "select", "checkbox", "date", "number", "email"];

/** Draggable field-type palette: drag a tile onto the form (or click it) to add
 *  a field of that kind with sensible defaults you can refine afterwards. */
const FIELD_PALETTE: { kind: FormFieldKind; label: string; glyph: string }[] = [
  { kind: "text", label: "Short text", glyph: "T" },
  { kind: "textarea", label: "Long text", glyph: "¶" },
  { kind: "select", label: "Dropdown", glyph: "▾" },
  { kind: "checkbox", label: "Checkbox", glyph: "☑" },
  { kind: "date", label: "Date", glyph: "📅" },
  { kind: "number", label: "Number", glyph: "#" },
  { kind: "email", label: "Email", glyph: "@" },
];
const FORM_KIND_MIME = "application/x-ac-form-kind";

/** Target entity attributes a field's answer can SET, by form target type.
 *  Empty `maps_to` ("") = unmapped (the answer just joins the summary comment). */
const MAP_TARGETS: Record<string, { value: string; label: string }[]> = {
  ticket: [
    { value: "subject", label: "Subject" },
    { value: "description", label: "Description" },
    { value: "department", label: "Department (routing)" },
    { value: "priority", label: "Priority (code)" },
    { value: "type", label: "Type" },
  ],
  task: [
    { value: "title", label: "Title" },
    { value: "description", label: "Description" },
  ],
  work_order: [
    { value: "title", label: "Title" },
    { value: "description", label: "Description" },
    { value: "priority", label: "Priority (code)" },
  ],
};

/** snake_case key suggestion from a human label ("Office location" → office_location). */
function suggestKey(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** kebab-case slug suggestion from a form name. */
function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Human summary of a stored branching_json rule. */
function branchingSummary(json: string | null): string {
  if (!json) return "—";
  try {
    const rule = JSON.parse(json) as FormBranchingRule;
    if (!rule?.showIf?.field) return "—";
    return `when ${rule.showIf.field} = ${String(rule.showIf.equals)}`;
  } catch {
    return "—";
  }
}

const FORM_SORT: Record<string, SortAccessor<FormDetailRow>> = {
  form: (f) => f.name,
  creates: (f) => f.target_type,
};

export function FormsPage() {
  const { data, loading, error, reload } = useApi((signal) => listForms(signal), []);
  const forms = (data?.forms ?? []) as FormDetailRow[];
  // Select by slug (the detail endpoint is slug-keyed).
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const deleteMut = useMutation(deleteForm);
  const selected = forms.find((f) => f.slug === selectedSlug) ?? null;
  const { rows: sortedForms, sort, toggle } = useTableSort(forms, FORM_SORT);
  // null = closed, "new" = create, a row = edit.
  const [editing, setEditing] = useState<FormDetailRow | "new" | null>(null);

  return (
    <>
      <PageBar
        crumbs="Forms"
        actions={
          <button className="btn primary" onClick={() => setEditing("new")}>
            + New form
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Forms</h1>
            <div className="sub">
              Intake forms that create tickets or tasks on submit — fields can branch on earlier answers.
            </div>
          </div>
        </div>

        <div className="grid-2">
          <div className="card">
            <div className="card-head">
              <h3>Intake forms</h3>
              <span className="faint">{forms.length}</span>
            </div>
            <div className="card-body tight">
              {loading ? (
                <Loading label="Loading forms…" />
              ) : error ? (
                <ErrorState error={error} onRetry={reload} />
              ) : forms.length === 0 ? (
                <EmptyState>No forms yet — create one on the right.</EmptyState>
              ) : (
                <table className="data">
                  <thead>
                    <tr>
                      <SortTh field="form" sort={sort} onSort={toggle}>Form</SortTh>
                      <SortTh field="creates" sort={sort} onSort={toggle}>Creates</SortTh>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedForms.map((f) => (
                      <tr
                        key={f.id}
                        className="clickable"
                        aria-selected={f.slug === selectedSlug}
                        tabIndex={0}
                        onClick={() => setSelectedSlug(f.slug)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setSelectedSlug(f.slug);
                        }}
                        style={f.slug === selectedSlug ? { background: "var(--surface-2)" } : undefined}
                      >
                        <td>
                          <strong>{f.name}</strong>
                          <div className="mono faint">{f.slug}</div>
                        </td>
                        <td>
                          <span className="pill">{f.target_type}</span>
                        </td>
                        <td className="row-actions">
                          <div className="btn-row" style={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
                          <button
                            className="btn small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(f);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn small danger"
                            disabled={deleteMut.pending}
                            onClick={(e) => {
                              e.stopPropagation();
                              void confirmDialog({
                                message: `Delete form "${f.name}" (and its fields + submissions)?`,
                                danger: true,
                              }).then((yes) => {
                                if (!yes) return;
                                deleteMut.run(f.id).then((ok) => {
                                  if (ok) {
                                    if (f.slug === selectedSlug) setSelectedSlug(null);
                                    reload();
                                  }
                                });
                              });
                            }}
                          >
                            Delete
                          </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {deleteMut.error ? <Alert kind="error">{deleteMut.error}</Alert> : null}
            </div>
          </div>

          {selected ? (
            <FormDetailCard key={selected.slug} form={selected} />
          ) : (
            <div className="card">
              <div className="card-head">
                <h3>Fields</h3>
              </div>
              <div className="card-body">
                <EmptyState>Select a form on the left to edit its fields.</EmptyState>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <FormDialog
          key={editing === "new" ? "new" : editing.id}
          form={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(slug) => {
            setSelectedSlug(slug);
            reload();
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

/** Create or edit a form's config (POST/PATCH /api/forms) — name, slug, what it
 *  creates, ticket default status, and the portal/published toggles. */
function FormDialog({
  form,
  onClose,
  onSaved,
}: {
  form: FormDetailRow | null;
  onClose: () => void;
  onSaved: (slug: string) => void;
}) {
  const editing = !!form;
  const [name, setName] = useState(form?.name ?? "");
  const [slug, setSlug] = useState(form?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(editing);
  const [targetType, setTargetType] = useState<FormTargetKind>(
    (form?.target_kind as FormTargetKind) ?? (form?.target_type as FormTargetKind) ?? "ticket",
  );
  const [projectId, setProjectId] = useState(form?.target_project_id ?? "");
  const [defaultStatusId, setDefaultStatusId] = useState(form?.default_status_id ?? "");
  const [description, setDescription] = useState(form?.description ?? "");
  const [logoUrl, setLogoUrl] = useState(form?.logo_url ?? "");
  const [notifyEmail, setNotifyEmail] = useState(form?.notify_email ?? "");
  const [published, setPublished] = useState(!!form?.is_published);

  const createMut = useMutation(createForm);
  const updateMut = useMutation(updateForm);
  const publishMut = useMutation(publishForm);
  const pending = createMut.pending || updateMut.pending || publishMut.pending;
  const error = createMut.error ?? updateMut.error ?? publishMut.error;

  // Project roster (task forms need a home) + ticket statuses (default status).
  const projectsQ = useApi((signal) => listProjects(false, signal), []);
  const projects = projectsQ.data?.projects ?? [];
  const statusesQ = useApi((signal) => listStatuses(signal), []);
  const statuses = statusesQ.data?.statuses ?? [];

  useEffect(() => {
    if (!slugTouched) setSlug(suggestSlug(name));
  }, [name, slugTouched]);

  const valid = name.trim() !== "" && slug.trim() !== "" && (targetType !== "task" || !!projectId);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid || pending) return;
    const base = {
      name: name.trim(),
      slug: slug.trim(),
      target_kind: targetType,
      target_project_id: targetType === "task" ? projectId : null,
      // Visibility is a single switch now: a published form is the live,
      // portal-shareable one; an unpublished form is an internal draft.
      portal_enabled: published,
      default_status_id: targetType === "ticket" ? defaultStatusId || null : null,
      description: description.trim() || null,
      logo_url: logoUrl.trim() || null,
      notify_email: notifyEmail.trim() || null,
    };
    if (editing) {
      const res = await updateMut.run(form.id, base);
      if (!res) return;
      if (published !== !!form.is_published) {
        const p = await publishMut.run(form.id, published);
        if (!p) return;
      }
      onSaved(res.form.slug);
    } else {
      const res = await createMut.run(base);
      if (!res) return;
      if (published) {
        const p = await publishMut.run(res.form.id, true);
        if (!p) return;
      }
      onSaved(res.form.slug);
    }
  }

  return (
    <Modal
      title={editing ? "Edit form" : "New form"}
      onClose={onClose}
      footer={
        <div className="btn-row" style={{ justifyContent: "flex-end", width: "100%" }}>
          <button className="btn" type="button" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="form-config-form" disabled={pending || !valid}>
            {pending ? <Spinner label="Saving…" /> : editing ? "Save changes" : "Create form"}
          </button>
        </div>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="form-config-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="IT support request"
            required
          />
        </label>
        <label className="field">
          <span className="label">Slug</span>
          <input
            type="text"
            className="mono"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            placeholder="it-support-request"
            required
          />
          <span className="hint">
            Shareable link: <span className="mono">/f/{slug || "{slug}"}</span>
            {editing ? " — changing the slug changes that link." : null}
          </span>
        </label>
        <label className="field">
          <span className="label">Description (optional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Shown to people filling out the form."
          />
        </label>
        <label className="field">
          <span className="label">Logo URL (optional)</span>
          <input
            type="url"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://…/logo.png"
          />
          <span className="hint">Shown at the top of the public form page.</span>
        </label>
        <label className="field">
          <span className="label">Creates</span>
          <select value={targetType} onChange={(e) => setTargetType(e.target.value as FormTargetKind)}>
            <option value="ticket">Ticket (helpdesk)</option>
            <option value="task">Project task</option>
            <option value="work_order">Work order (facilities)</option>
          </select>
        </label>
        {targetType === "task" ? (
          <label className="field">
            <span className="label">Target project</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
              <option value="">— select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <span className="hint">Project-task forms must land in a project.</span>
          </label>
        ) : targetType === "ticket" ? (
          <label className="field">
            <span className="label">Default ticket status</span>
            <select value={defaultStatusId} onChange={(e) => setDefaultStatusId(e.target.value)} disabled={statusesQ.loading}>
              <option value="">(system default — “Open”)</option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <span className="hint">New tickets from this form land in this status (e.g. a triage lane).</span>
          </label>
        ) : (
          <span className="hint">Submissions create a facility work order in the work-order queue.</span>
        )}
        <label className="field">
          <span className="label">Notify email (optional)</span>
          <input
            type="email"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            placeholder="alerts@yourteam.edu"
          />
          <span className="hint">A notification is emailed here on every submission.</span>
        </label>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <span className="label">Visibility</span>
          <label className="toggle">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
            Published (live &amp; shareable; unpublished forms are internal drafts)
          </label>
        </fieldset>
      </form>
    </Modal>
  );
}

/** Right card: fields of the selected form + a drag-and-drop element palette and
 *  the "Add field" form for precise control. */
function FormDetailCard({ form }: { form: FormDetailRow }) {
  const { data, loading, error, reload } = useApi((signal) => getFormBySlug(form.slug, signal), [form.slug]);
  const fields = data?.fields ?? [];
  const [preview, setPreview] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [editingField, setEditingField] = useState<FormFieldRow | null>(null);
  const quickAddMut = useMutation(addFormField);
  const deleteFieldMut = useMutation(deleteFormField);

  // Local ordered copy so drag-to-reorder is instant; resynced on every reload.
  const [orderedFields, setOrderedFields] = useState<FormFieldRow[]>(data?.fields ?? []);
  const [dragId, setDragId] = useState<string | null>(null);
  useEffect(() => setOrderedFields(data?.fields ?? []), [data?.fields]);

  /** Move the dragged field to `targetId`'s slot and persist the new positions. */
  async function reorderTo(targetId: string) {
    const id = dragId;
    setDragId(null);
    if (!id || id === targetId) return;
    const cur = [...orderedFields];
    const from = cur.findIndex((f) => f.id === id);
    const to = cur.findIndex((f) => f.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const [moved] = cur.splice(from, 1);
    cur.splice(to, 0, moved);
    setOrderedFields(cur); // optimistic
    // Persist only the rows whose position actually changed.
    await Promise.all(
      cur.map((f, i) => (f.position !== i ? updateFormField(form.id, f.id, { position: i }) : null)).filter(Boolean),
    ).catch(() => {});
    reload();
  }

  /** Add a field of `kind` with defaults + a key unique among existing fields. */
  function quickAdd(kind: FormFieldKind) {
    if (quickAddMut.pending) return;
    const meta = FIELD_PALETTE.find((p) => p.kind === kind);
    const label = meta?.label ?? kind;
    const base = suggestKey(label) || kind;
    const used = new Set(fields.map((f) => f.key));
    let key = base;
    for (let n = 2; used.has(key); n++) key = `${base}_${n}`;
    const input: AddFormFieldInput = {
      key,
      label,
      kind,
      required: false,
      // `select` needs at least one option to be valid — seed two editable ones.
      ...(kind === "select" ? { options: ["Option 1", "Option 2"] } : {}),
    };
    void quickAddMut.run(form.id, input).then((r) => r && reload());
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{form.name}</h3>
        <div className="btn-row" style={{ flexWrap: "nowrap" }}>
          <button className="btn small" onClick={() => setShowSubs(true)} disabled={loading}>
            Submissions
          </button>
          <button className="btn small" onClick={() => setPreview(true)} disabled={loading}>
            Preview
          </button>
        </div>
      </div>

      {/* Drag-and-drop element palette: drag a tile onto the form, or click to add. */}
      <div className="form-palette" role="toolbar" aria-label="Field types — drag onto the form to add">
        {FIELD_PALETTE.map((p) => (
          <button
            key={p.kind}
            type="button"
            className="form-palette-item"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(FORM_KIND_MIME, p.kind);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => quickAdd(p.kind)}
            disabled={quickAddMut.pending}
            title={`Add a ${p.label} field — drag onto the form or click`}
          >
            <span className="form-palette-glyph" aria-hidden="true">{p.glyph}</span>
            {p.label}
          </button>
        ))}
      </div>

      <div
        className={`card-body tight form-drop${dropping ? " form-drop-active" : ""}`}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(FORM_KIND_MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            setDropping(true);
          }
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropping(false);
          const kind = e.dataTransfer.getData(FORM_KIND_MIME);
          if ((FIELD_KINDS as string[]).includes(kind)) quickAdd(kind as FormFieldKind);
        }}
      >
        {loading ? (
          <Loading label="Loading fields…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : orderedFields.length === 0 ? (
          <EmptyState>Drag a field type from the palette above to add it.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th aria-label="Reorder" style={{ width: "1%" }} />
                <th>Label</th>
                <th>Key</th>
                <th>Kind</th>
                <th>Required</th>
                <th>Maps to</th>
                <th>Branching</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {orderedFields.map((f: FormFieldRow) => (
                <tr
                  key={f.id}
                  draggable
                  onDragStart={(e: DragEvent) => {
                    setDragId(f.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e: DragEvent) => {
                    if (dragId && dragId !== f.id) e.preventDefault();
                  }}
                  onDrop={() => void reorderTo(f.id)}
                  className={dragId === f.id ? "row-dragging" : undefined}
                >
                  <td className="drag-handle" title="Drag to reorder" aria-hidden="true">
                    ⠿
                  </td>
                  <td>
                    <strong>{f.label}</strong>
                  </td>
                  <td className="mono">{f.key}</td>
                  <td>
                    <span className="pill">{f.kind}</span>
                  </td>
                  <td>{f.required ? "yes" : "—"}</td>
                  <td>{f.maps_to ? <span className="pill">{f.maps_to}</span> : <span className="faint">—</span>}</td>
                  <td className="faint">{branchingSummary(f.branching_json)}</td>
                  <td className="row-actions">
                    <div className="btn-row" style={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
                      <button className="btn small" onClick={() => setEditingField(f)}>
                        Edit
                      </button>
                      <button
                        className="btn small danger"
                        disabled={deleteFieldMut.pending}
                        onClick={() =>
                          void confirmDialog({ message: `Delete field "${f.label}"?`, danger: true }).then((yes) => {
                            if (yes) deleteFieldMut.run(form.id, f.id).then((ok) => ok && reload());
                          })
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {quickAddMut.error ? <Alert kind="error">{quickAddMut.error}</Alert> : null}
      </div>

      {preview ? (
        <FormPreviewDialog form={form} fields={fields} onClose={() => setPreview(false)} />
      ) : null}
      {showSubs ? <FormSubmissionsDialog form={form} onClose={() => setShowSubs(false)} /> : null}
      {editingField ? (
        <FieldEditDialog
          key={editingField.id}
          formId={form.id}
          targetType={form.target_kind ?? form.target_type}
          field={editingField}
          onClose={() => setEditingField(null)}
          onSaved={() => {
            setEditingField(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** View a form's recorded submissions: each answer payload + what it created. */
function FormSubmissionsDialog({ form, onClose }: { form: FormDetailRow; onClose: () => void }) {
  const { data, loading, error, reload } = useApi((signal) => listFormSubmissions(form.id, signal), [form.id]);
  const statsApi = useApi((signal) => getFormStats(form.id, signal), [form.id]);
  const submissions = data?.submissions ?? [];
  const stats = statsApi.data?.stats;
  const conversion =
    stats && stats.views > 0 ? Math.round((stats.submissions / stats.views) * 100) : null;

  function entityLink(type: string | null, id: string | null) {
    if (!type || !id) return <span className="faint">—</span>;
    if (type === "ticket") return <Link to={`/tickets/${id}`}>ticket</Link>;
    if (type === "task") return <Link to={`/action-items?open=${id}`}>task</Link>;
    if (type === "facility_request") return <Link to={`/facilities/${id}`}>work order</Link>;
    return <span className="faint">{type}</span>;
  }

  return (
    <Modal title={`Submissions · ${form.name}`} onClose={onClose} wide>
      {stats ? (
        <div className="form-stats">
          <div className="form-stat">
            <span className="form-stat-n">{stats.views}</span>
            <span className="form-stat-l">views</span>
          </div>
          <div className="form-stat">
            <span className="form-stat-n">{stats.submissions}</span>
            <span className="form-stat-l">submissions</span>
          </div>
          <div className="form-stat">
            <span className="form-stat-n">{conversion != null ? `${conversion}%` : "—"}</span>
            <span className="form-stat-l">conversion</span>
          </div>
          {stats.byEntity.length > 0 ? (
            <div className="form-stat-breakdown">
              {stats.byEntity.map((b) => (
                <span key={b.entity_type} className="pill">
                  {b.entity_type === "(none)" ? "not created" : b.entity_type}: {b.n}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {loading ? (
        <Loading label="Loading submissions…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : submissions.length === 0 ? (
        <EmptyState>No submissions yet.</EmptyState>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>When</th>
              <th>Answers</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map((s) => {
              let payload: Record<string, unknown> = {};
              try {
                payload = s.payload_json ? (JSON.parse(s.payload_json) as Record<string, unknown>) : {};
              } catch {
                payload = {};
              }
              const entries = Object.entries(payload);
              return (
                <tr key={s.id}>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{formatDate(s.created_at)}</td>
                  <td>
                    {entries.length === 0 ? (
                      <span className="faint">—</span>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {entries.map(([k, v]) => (
                          <li key={k}>
                            <span className="mono faint">{k}</span>: {String(v)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>{entityLink(s.created_entity_type, s.created_entity_id)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

/** Configure an existing field in a dialog (PATCH /api/forms/:id/fields/:fieldId). */
function FieldEditDialog({
  formId,
  field,
  targetType,
  onClose,
  onSaved,
}: {
  formId: string;
  field: FormFieldRow;
  targetType: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(field.label);
  const [key, setKey] = useState(field.key);
  const [kind, setKind] = useState<FormFieldKind>(field.kind as FormFieldKind);
  const [required, setRequired] = useState(!!field.required);
  const [optionsText, setOptionsText] = useState<string>(() => {
    try {
      const arr = field.options_json ? (JSON.parse(field.options_json) as string[]) : [];
      return arr.join("\n");
    } catch {
      return "";
    }
  });
  // Seed the branching inputs once from the stored rule.
  const initialBranch = (() => {
    try {
      const rule = field.branching_json ? (JSON.parse(field.branching_json) as FormBranchingRule) : null;
      if (rule?.showIf?.field) return { field: rule.showIf.field, equals: String(rule.showIf.equals ?? "") };
    } catch {
      /* ignore */
    }
    return { field: "", equals: "" };
  })();
  const [showIfField, setShowIfField] = useState(initialBranch.field);
  const [showIfEquals, setShowIfEquals] = useState(initialBranch.equals);
  const [mapsTo, setMapsTo] = useState(field.maps_to ?? "");
  const saveMut = useMutation((patch: UpdateFormFieldInput) => updateFormField(formId, field.id, patch));
  const mapTargets = MAP_TARGETS[targetType] ?? MAP_TARGETS.ticket;

  const options = optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
  const valid =
    label.trim() !== "" &&
    key.trim() !== "" &&
    (kind !== "select" || options.length > 0) &&
    (showIfField.trim() === "") === (showIfEquals.trim() === "");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const patch: UpdateFormFieldInput = {
      key: key.trim(),
      label: label.trim(),
      kind,
      required,
      maps_to: mapsTo || null,
      // Always send options/branching so clearing them persists.
      options: kind === "select" ? options : [],
      ...(showIfField.trim()
        ? { branching: { showIf: { field: showIfField.trim(), equals: showIfEquals.trim() } } }
        : { branching: null }),
    };
    const res = await saveMut.run(patch);
    if (res) onSaved();
  }

  return (
    <Modal
      title={`Edit field · ${field.label}`}
      onClose={onClose}
      footer={
        <div className="btn-row" style={{ justifyContent: "flex-end", width: "100%" }}>
          <button className="btn" type="button" onClick={onClose} disabled={saveMut.pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="field-edit-form" disabled={saveMut.pending || !valid}>
            {saveMut.pending ? <Spinner label="Saving…" /> : "Save field"}
          </button>
        </div>
      }
    >
      {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
      <form id="field-edit-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Label</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} required />
        </label>
        <label className="field">
          <span className="label">Key</span>
          <input type="text" className="mono" value={key} onChange={(e) => setKey(e.target.value)} required />
          {targetType === "ticket" ? (
            <span className="hint">
              Special keys: <code>subject</code> becomes the ticket subject; <code>department</code> auto-routes the
              ticket to that department&rsquo;s team.
            </span>
          ) : null}
        </label>
        <label className="field">
          <span className="label">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as FormFieldKind)}>
            {FIELD_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Maps to</span>
          <select value={mapsTo} onChange={(e) => setMapsTo(e.target.value)}>
            <option value="">(none — added to the summary)</option>
            {mapTargets.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="hint">
            On submit, this answer sets the {targetType === "task" ? "task" : "ticket"}&rsquo;s chosen attribute
            instead of just appearing in the summary.
          </span>
        </label>
        {kind === "select" ? (
          <label className="field">
            <span className="label">Options (one per line)</span>
            <textarea value={optionsText} onChange={(e) => setOptionsText(e.target.value)} rows={4} placeholder={"Hardware\nSoftware\nAccess"} />
          </label>
        ) : null}
        <label className="toggle" style={{ marginBottom: 10 }}>
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Required (when visible)
        </label>
        <label className="field">
          <span className="label">Show only if (optional)</span>
          <div className="btn-row">
            <input
              type="text"
              className="mono"
              value={showIfField}
              onChange={(e) => setShowIfField(e.target.value)}
              placeholder="field key, e.g. category"
              style={{ flex: 1 }}
            />
            <span className="faint">equals</span>
            <input
              type="text"
              value={showIfEquals}
              onChange={(e) => setShowIfEquals(e.target.value)}
              placeholder="value, e.g. Hardware"
              style={{ flex: 1 }}
            />
          </div>
          <span className="hint">The field is shown — and validated — only when that earlier answer matches.</span>
        </label>
      </form>
    </Modal>
  );
}

/** Map a form field kind to a native input type for the live preview. */
function previewInputType(kind: string): string {
  if (kind === "date") return "date";
  if (kind === "number") return "number";
  if (kind === "email") return "email";
  return "text";
}

/** Form preview: the public submit URL (copyable) + a live, non-submitting
 *  render of the fields exactly as a submitter would see them. */
function FormPreviewDialog({
  form,
  fields,
  onClose,
}: {
  form: FormDetailRow;
  fields: FormFieldRow[];
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<"page" | "api" | null>(null);
  // Live answers, keyed by field.key — drives branching visibility just like the
  // server's visibleFields() does on submit.
  const [answers, setAnswers] = useState<Record<string, string | boolean>>({});
  const setAnswer = (key: string, value: string | boolean) => setAnswers((a) => ({ ...a, [key]: value }));
  // Shareable, human-fillable page (same app origin) + the raw POST API endpoint.
  const pageUrl = `${window.location.origin}/f/${form.slug}`;
  const apiUrl = `${API_BASE || window.location.origin}/api/forms/${form.slug}/submit`;

  /** A field is visible when it has no branching rule, or its showIf condition is
   *  currently satisfied (loose string compare, mirroring forms-db.visibleFields). */
  function isVisible(f: FormFieldRow): boolean {
    if (!f.branching_json) return true;
    try {
      const rule = JSON.parse(f.branching_json) as FormBranchingRule;
      if (!rule?.showIf?.field) return true;
      return String(answers[rule.showIf.field] ?? "") === String(rule.showIf.equals);
    } catch {
      return true;
    }
  }
  const visible = fields.filter(isVisible);

  function copy(which: "page" | "api", value: string) {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(which);
      window.setTimeout(() => setCopied(null), 1500);
    });
  }

  return (
    <Modal title={`Preview · ${form.name}`} onClose={onClose} wide>
      {/* Live, non-submitting render: conditional fields appear as their rules match. */}
      <form className="form-preview" onSubmit={(e) => e.preventDefault()}>
        {fields.length === 0 ? (
          <EmptyState>This form has no fields yet.</EmptyState>
        ) : (
          <>
            <p className="hint" style={{ marginTop: 0 }}>
              Live preview — fill it in to see conditional (branched) fields show and hide.
            </p>
            {visible.map((f) => {
              let opts: string[] = [];
              try {
                opts = f.options_json ? (JSON.parse(f.options_json) as string[]) : [];
              } catch {
                opts = [];
              }
              return (
                <label className="field" key={f.id}>
                  <span className="label">
                    {f.label}
                    {f.required ? <span style={{ color: "var(--danger, #dc2626)" }}> *</span> : null}
                  </span>
                  {f.kind === "textarea" ? (
                    <textarea
                      rows={3}
                      placeholder={f.label}
                      value={String(answers[f.key] ?? "")}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                    />
                  ) : f.kind === "select" ? (
                    <select value={String(answers[f.key] ?? "")} onChange={(e) => setAnswer(f.key, e.target.value)}>
                      <option value="">— select —</option>
                      {opts.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.kind === "checkbox" ? (
                    <span className="toggle">
                      <input
                        type="checkbox"
                        checked={!!answers[f.key]}
                        onChange={(e) => setAnswer(f.key, e.target.checked)}
                      />{" "}
                      {f.label}
                    </span>
                  ) : (
                    <input
                      type={previewInputType(f.kind)}
                      placeholder={f.label}
                      value={String(answers[f.key] ?? "")}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                    />
                  )}
                </label>
              );
            })}
          </>
        )}
        <button className="btn primary" type="submit" disabled title="Preview only — submitting is disabled here">
          Submit
        </button>
      </form>

      <div className="field" style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
        <span className="label">Shareable form link</span>
        <div className="btn-row" style={{ gap: 8 }}>
          <input
            type="text"
            readOnly
            value={pageUrl}
            className="mono"
            style={{ flex: 1 }}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Shareable form link"
          />
          <a className="btn small" href={pageUrl} target="_blank" rel="noreferrer" style={{ flex: "0 0 auto" }}>
            Open
          </a>
          <button type="button" className="btn small" style={{ flex: "0 0 auto" }} onClick={() => copy("page", pageUrl)}>
            {copied === "page" ? "Copied ✓" : "Copy"}
          </button>
        </div>
        <span className="hint">
          Send this link to anyone — it opens a fillable page that submits to this form, no login required.{" "}
          {form.portal_enabled ? null : (
            <strong>Enable the “portal” flag so anonymous people can submit.</strong>
          )}
          {form.is_published ? null : <strong> This form is a draft — publish it before sharing.</strong>}
        </span>
      </div>

      <div className="field" style={{ marginBottom: 0 }}>
        <span className="label">API submit endpoint (integrations)</span>
        <div className="btn-row" style={{ gap: 8 }}>
          <input
            type="text"
            readOnly
            value={apiUrl}
            className="mono"
            style={{ flex: 1 }}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="API submit endpoint"
          />
          <button type="button" className="btn small" style={{ flex: "0 0 auto" }} onClick={() => copy("api", apiUrl)}>
            {copied === "api" ? "Copied ✓" : "Copy"}
          </button>
        </div>
        <span className="hint">
          A <strong>POST</strong> endpoint — submit the answers as a JSON <code>payload</code> (for integrations,
          not a browser).
        </span>
      </div>
    </Modal>
  );
}
